import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import { History, Save } from '@mui/icons-material';
import { BRAND_COLORS } from '../../../styles/theme';
import type {
  AdminDialogProps,
  DroneLegacyHoursItem,
  PilotLegacyHoursItem,
} from '../types/adminTools.types';
import type {
  LegacyHoursFormState,
  LegacyHoursValidationState,
} from '../hooks/useLegacyHoursMigration';

// Shared form component for legacy hours input
interface LegacyHoursFormProps {
  formState: LegacyHoursFormState;
  validationErrors: LegacyHoursValidationState;
  hasValidationErrors: boolean;
  saving: boolean;
  onPre2023Change: (value: string) => void;
  onYear2023Change: (value: string) => void;
  onYear2024Change: (value: string) => void;
  onSave: () => Promise<void>;
}

const LegacyHoursForm: React.FC<LegacyHoursFormProps> = ({
  formState,
  validationErrors,
  hasValidationErrors,
  saving,
  onPre2023Change,
  onYear2023Change,
  onYear2024Change,
  onSave,
}) => (
  <Paper sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
    <Typography variant="subtitle1" gutterBottom>
      Enter hours in HH:MM:SS format (e.g., 26:23:55 for 26h 23m 55s)
    </Typography>
    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
      <TextField
        label="Pre-2023 Hours"
        placeholder="00:00:00"
        value={formState.pre_2023}
        onChange={(e) => onPre2023Change(e.target.value)}
        disabled={saving}
        error={validationErrors.pre_2023}
        helperText={validationErrors.pre_2023 ? 'Invalid format' : ''}
        sx={{ flex: 1 }}
      />
      <TextField
        label="2023 Hours"
        placeholder="00:00:00"
        value={formState.year_2023}
        onChange={(e) => onYear2023Change(e.target.value)}
        disabled={saving}
        error={validationErrors.year_2023}
        helperText={validationErrors.year_2023 ? 'Invalid format' : ''}
        sx={{ flex: 1 }}
      />
      <TextField
        label="2024 Hours"
        placeholder="00:00:00"
        value={formState.year_2024}
        onChange={(e) => onYear2024Change(e.target.value)}
        disabled={saving}
        error={validationErrors.year_2024}
        helperText={validationErrors.year_2024 ? 'Invalid format' : ''}
        sx={{ flex: 1 }}
      />
    </Box>
    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        variant="contained"
        onClick={onSave}
        disabled={saving || hasValidationErrors}
        startIcon={saving ? <CircularProgress size={16} /> : <Save />}
        sx={{
          backgroundColor: BRAND_COLORS.forestGreen.main,
          '&:hover': {
            backgroundColor: BRAND_COLORS.forestGreen.main,
            opacity: 0.8,
          },
        }}
      >
        {saving ? 'Saving...' : 'Save Legacy Hours'}
      </Button>
    </Box>
  </Paper>
);

interface LegacyHoursMigrationDialogProps extends AdminDialogProps {
  // Tab state
  activeTab: 'drones' | 'pilots';
  onTabChange: (tab: 'drones' | 'pilots') => void;

  // Loading states
  loading: boolean;
  saving: boolean;

  // Data
  dronesData: DroneLegacyHoursItem[];
  pilotsData: PilotLegacyHoursItem[];

  // Selection
  selectedDrone: string;
  selectedPilot: string;
  onSelectDrone: (serialNumber: string) => void;
  onSelectPilot: (userId: string) => void;

  // Form state
  formState: LegacyHoursFormState;
  validationErrors: LegacyHoursValidationState;
  hasValidationErrors: boolean;
  onPre2023Change: (value: string) => void;
  onYear2023Change: (value: string) => void;
  onYear2024Change: (value: string) => void;

  // Save actions
  onSaveDroneHours: () => Promise<void>;
  onSavePilotHours: () => Promise<void>;
}

export const LegacyHoursMigrationDialog: React.FC<LegacyHoursMigrationDialogProps> = ({
  open,
  onClose,
  activeTab,
  onTabChange,
  loading,
  saving,
  dronesData,
  pilotsData,
  selectedDrone,
  selectedPilot,
  onSelectDrone,
  onSelectPilot,
  formState,
  validationErrors,
  hasValidationErrors,
  onPre2023Change,
  onYear2023Change,
  onYear2024Change,
  onSaveDroneHours,
  onSavePilotHours,
}) => {
  const hasDroneLegacyHours = (drone: DroneLegacyHoursItem) =>
    drone.legacy_hours?.pre_2023 !== undefined ||
    drone.legacy_hours?.['2023'] !== undefined ||
    drone.legacy_hours?.['2024'] !== undefined;

  const hasPilotLegacyHours = (pilot: PilotLegacyHoursItem) =>
    pilot.legacy_flight_hours?.pre_2023 !== undefined ||
    pilot.legacy_flight_hours?.['2023'] !== undefined ||
    pilot.legacy_flight_hours?.['2024'] !== undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="legacy-hours-dialog">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <History />
          Legacy Hours Migration
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_e, newValue) => onTabChange(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            <Tab label="Drones" value="drones" />
            <Tab label="Pilots" value="pilots" />
          </Tabs>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Drones Tab */}
              {activeTab === 'drones' && (
                <Box>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Select Drone</InputLabel>
                    <Select
                      value={selectedDrone}
                      label="Select Drone"
                      onChange={(e) => onSelectDrone(e.target.value)}
                    >
                      {dronesData.map((drone) => (
                        <MenuItem key={drone.serial_number} value={drone.serial_number}>
                          {drone.serial_number}
                          {hasDroneLegacyHours(drone) && (
                            <Chip
                              size="small"
                              label="Has Legacy Hours"
                              color="success"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedDrone && (
                    <LegacyHoursForm
                      formState={formState}
                      validationErrors={validationErrors}
                      hasValidationErrors={hasValidationErrors}
                      saving={saving}
                      onPre2023Change={onPre2023Change}
                      onYear2023Change={onYear2023Change}
                      onYear2024Change={onYear2024Change}
                      onSave={onSaveDroneHours}
                    />
                  )}
                </Box>
              )}

              {/* Pilots Tab */}
              {activeTab === 'pilots' && (
                <Box>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Select Pilot</InputLabel>
                    <Select
                      value={selectedPilot}
                      label="Select Pilot"
                      onChange={(e) => onSelectPilot(e.target.value)}
                    >
                      {pilotsData.map((pilot) => (
                        <MenuItem key={pilot.user_id} value={pilot.user_id}>
                          {pilot.full_name || pilot.email}
                          {hasPilotLegacyHours(pilot) && (
                            <Chip
                              size="small"
                              label="Has Legacy Hours"
                              color="success"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedPilot && (
                    <LegacyHoursForm
                      formState={formState}
                      validationErrors={validationErrors}
                      hasValidationErrors={hasValidationErrors}
                      saving={saving}
                      onPre2023Change={onPre2023Change}
                      onYear2023Change={onYear2023Change}
                      onYear2024Change={onYear2024Change}
                      onSave={onSavePilotHours}
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
